import { useEffect } from "react";
import AppLoading from '../components/AppLoading';
import { BillingRouter, PrivateAppRouter, PublicAppRouter } from '../routers/AppRouter'
import { useUser } from '../contexts/user.context';
import { useOnboarding } from "../contexts/onboarding.context";
import useAuthentication from '../hooks/authentication.hook';
import { mixpanelEvents, onboardingConnectModalStates, platformAuthFailureReasons, PLATFORMS, storeStatus } from '../utils/constants';
import { getRedirectErrorReason, showPendingPlatformAuthError, showPendingRedirectError, showPlatformAuthError, storePlatformAuthError, storeRedirectError } from "../utils/errors";
import { clearPendingPlatformAuth, getPendingPlatformAuth, retryPendingTrayCompatibleAuth, trackPlatformAuthFailure } from "../utils/platformAuth";
import mixpanel from "mixpanel-browser";

export default function AppAuthentication() {
    const { isAuthLoading } = useAuthentication()
    const { user } = useUser()
    const { setData, setScreenState } = useOnboarding();

    // The user comes back here after authorizing on his platform, so what the
    // platform sent is merged into the data collected before the redirect,
    // ignoring the parameters the platform did not send.
    const mergeOnboardingData = (onboardingData) => {
        const sentData = Object.fromEntries(
            Object.entries(onboardingData).filter(([, value]) => value !== null && value !== undefined)
        );

        setData(prev => ({ ...prev, ...sentData }))
    }

    // The authorization that does not go through leaves the user back on the connect
    // screen with the modal still open on its loading state, so it is closed. Any
    // other state is a screen he reached on his own, so it is kept.
    const closeAuthorizationModal = () => {
        setScreenState(current => onboardingConnectModalStates.includes(current) ? 'default' : current)
    }

    // A cancelled authorization is told on a toast. The message waits for the session
    // check when the user came back on a page load, since there is only the loading
    // screen until then, but is shown right away when the panel was never left behind.
    const handleCancelledPlatformAuth = ({ showsErrorNow } = {}) => {
        const platformId = getPendingPlatformAuth()?.platformId

        trackPlatformAuthFailure({
            failureReason: platformAuthFailureReasons.CANCELED
        })

        closeAuthorizationModal()

        return showsErrorNow ? showPlatformAuthError(platformId) : storePlatformAuthError(platformId)
    }

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search)

        const allowsIframe = searchParams.get("iframe")
        const platformId = searchParams.get("platform_id")
        const platformName = searchParams.get("platform_name")
        const storeId = searchParams.get("store_id")
        const storeName = searchParams.get("store_name")
        const storeUrl = searchParams.get("store_url")
        const platformToken = searchParams.get("platform_token")
        // Platform refresh token and site id exists only wix platform
        const platformRefreshToken = searchParams.get("platform_refresh_token")
        const siteId = searchParams.get("site_id")
        // storeApiUsername and storeApiPassword exists only irroba platform
        const storeApiUsername = searchParams.get("store_api_username");
        const storeApiPassword = searchParams.get("store_api_password");
        // platform_plan_id exists only lojaintegrada platform
        const ecommercePlatformPlanId = searchParams.get("platform_plan_id");
        // These exist only on Tray and Bagy platforms
        const storeApiUrl = searchParams.get("platform_api_address");
        const storeAuthCode = searchParams.get("platform_auth_code");
        const pointSaleId = searchParams.get("point_sale_id");
        const partnerId = searchParams.get("partner_id");
        const error = searchParams.get("error");
        const accessToken = searchParams.get("at");

        let authSucceeded = false;

        if (platformId === PLATFORMS.LOJAINTEGRADA) {
            if (storeName && storeUrl && platformId) {
                const onboardingData = {
                    ecommercePlatformAllowsIframe: allowsIframe === '1',
                    ecommercePlatformId: platformId,
                    ecommercePlatformName: platformName,
                    ecommercePlatformToken: platformToken,
                    ecommercePlatformPlanId: ecommercePlatformPlanId,
                    storeName: storeName,
                    storeUrl: storeUrl,
                }

                mixpanel.track(mixpanelEvents.ONBOARDING_PLATFORM_AUTH_SUCCESS, {
                    'platform_id': platformId
                })
                mergeOnboardingData(onboardingData)
                authSucceeded = true
            }
        } else {
            if (storeId && storeUrl && platformId && platformToken) {
                const onboardingData = {
                    ecommercePlatformAllowsIframe: allowsIframe === '1',
                    ecommercePlatformId: platformId,
                    ecommercePlatformName: platformName,
                    ecommercePlatformToken: platformToken,
                    ecommercePlatformRefreshToken: platformRefreshToken,
                    storeId: storeId,
                    storeName: storeName,
                    storeUrl: storeUrl,
                    siteId: siteId,
                    storeApiUsername: storeApiUsername,
                    storeApiPassword: storeApiPassword,
                    storeApiUrl: storeApiUrl,
                    storeAuthCode: storeAuthCode,
                    pointSaleId: pointSaleId,
                    partnerId: partnerId
                }

                mixpanel.track(mixpanelEvents.ONBOARDING_PLATFORM_AUTH_SUCCESS, {
                    'platform_id': platformId,
                    'store_id': storeId
                })
                mergeOnboardingData(onboardingData)
                authSucceeded = true
            }
        }

        if (error && retryPendingTrayCompatibleAuth(error)) {
            return
        } else if (error) {
            trackPlatformAuthFailure({
                failureReason: getRedirectErrorReason(error)
            })

            storeRedirectError(error)
            closeAuthorizationModal()

            const url = new URL(window.location.href)
            url.searchParams.delete("error")
            window.history.replaceState({}, '', url)
        } else if (authSucceeded || accessToken) {
            clearPendingPlatformAuth()
        } else if (getPendingPlatformAuth()) {
            handleCancelledPlatformAuth()
        }
    }, [])

    // The back button may restore the panel from the bfcache, and then no effect runs
    // again, so the cancelled authorization is also looked for on the page show.
    useEffect(() => {
        const handlePageShow = (event) => {
            if (event.persisted && getPendingPlatformAuth()) {
                handleCancelledPlatformAuth({ showsErrorNow: true })
            }
        }

        window.addEventListener('pageshow', handlePageShow)

        return () => window.removeEventListener('pageshow', handlePageShow)
    }, [])

    useEffect(() => {
        if (!isAuthLoading) {
            showPendingRedirectError()
            showPendingPlatformAuthError()
        }
    }, [isAuthLoading])

    if (isAuthLoading)
        return <AppLoading />

    if (!user)
        return <PublicAppRouter />

    // If the onboarding is completed, allow the panel to load only 
    // if the store status is on_trial or active
    if (
        user?.isOnboardingCompleted &&
        user?.store &&
        ![storeStatus.ON_TRIAL, storeStatus.ACTIVE].includes(user?.store?.status)
    ) {
        return <BillingRouter />
    }

    return <PrivateAppRouter />
}